import React, { useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { useOrders } from '@/hooks/useOrders';
import { Button, Card, LoadingSkeleton } from '@/components/ui';
import { UserDropdown } from '@/components/UserDropdown';
import { UnifiedUploadPanel } from '@/components/dashboard/UnifiedUploadPanel';
import { Tooltip } from '@/components/ui/Tooltip';
import { getServiceName, getServiceIcon, formatDate, getStoragePercentage, getStorageTier, formatFileSize } from '@/utils/formatting';
import { OrderStatus } from '@/shared/types';

import logo from '@/assets/logo.png';

export function Dashboard(): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { credits, storageUsed, storageLimit, loading: loadingCredits } = useCredits();
  const { orders: recentOrders, loading: loadingOrders } = useOrders(currentUser?.uid, { limit: 5 });

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenDashboardTour');
    if (!hasSeenTour && !loadingCredits && !loadingOrders) {
      const driverObj = driver({
        showProgress: true,
        steps: [
          { popover: { title: 'Welcome to List8!', description: 'Let us show you around your new dashboard.' } },
          { element: '#upload-area', popover: { title: 'Upload Photos', description: 'Drag & drop your property photos here to start.' } },
          { element: '#service-grid', popover: { title: 'Select a Service', description: 'Choose from Virtual Staging, Item Removal, and more.' } },
          { element: '#credits-card', popover: { title: 'Check Credits', description: 'See your available credits here. Top up anytime.' } },
          { element: '#recent-orders', popover: { title: 'Track Orders', description: 'View and download your completed images here.' } },
        ],
        onDestroyStarted: () => {
          if (!driverObj.hasNextStep() || confirm("Are you sure you want to skip the tour?")) {
            driverObj.destroy();
            localStorage.setItem('hasSeenDashboardTour', 'true');
          }
        },
      });

      driverObj.drive();
    }
  }, [loadingCredits, loadingOrders]);

  // Calculate storage percentage and tier
  const storagePercentage = getStoragePercentage(storageUsed, storageLimit);
  const storageTier = getStorageTier(storagePercentage);

  // Get storage tier color
  const getStorageTierColor = (): string => {
    switch (storageTier) {
      case 'danger':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-8">
              <Tooltip content="Return to Dashboard" position="bottom">
                <Link to="/dashboard" className="flex items-center">
                  <img src={logo} alt="List8.app Logo" className="h-16 w-auto" />
                </Link>
              </Tooltip>

              {/* Welcome Message Moved to Header */}
              <div className="hidden md:block border-l border-gray-200 pl-8">
                <h2 className="text-lg font-semibold text-gray-900">
                  Welcome back{currentUser?.displayName ? `, ${currentUser.displayName}` : ''}
                </h2>
                <p className="text-sm text-gray-500">
                  Ready to create your next listing?
                </p>
              </div>
            </div>
            <UserDropdown />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Unified Upload Panel */}
        <UnifiedUploadPanel />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Credits Card */}
          <Card id="credits-card" className="bg-gradient-to-br from-green-50 to-green-100 hover:shadow-xl transition-shadow duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Credit Balance</h3>
                <span className="text-3xl">💳</span>
              </div>
              {loadingCredits ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-24" />
              ) : (
                <p className="text-4xl font-bold text-green-700">{credits}</p>
              )}
              <p className="text-sm text-gray-600 mt-2">credits available</p>
              <Button
                variant="primary"
                className="w-full mt-4 bg-gradient-to-r from-green-500 to-emerald-600"
                onClick={() => navigate('/credits')}
              >
                Buy More Credits
              </Button>
            </div>
          </Card>

          {/* Storage Card */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-xl transition-shadow duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Storage Used</h3>
                <span className="text-3xl">💾</span>
              </div>
              {loadingCredits ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-32" />
              ) : (
                <>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatFileSize(storageUsed)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    of {formatFileSize(storageLimit)}
                  </p>
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${getStorageTierColor()}`}
                      style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{storagePercentage}% used</p>
                </>
              )}
            </div>
          </Card>

          {/* Orders Card */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-xl transition-shadow duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Total Orders</h3>
                <span className="text-3xl">📦</span>
              </div>
              {loadingOrders ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-16" />
              ) : (
                <p className="text-4xl font-bold text-purple-700">{recentOrders.length}+</p>
              )}
              <p className="text-sm text-gray-600 mt-2">orders processed</p>
              <Button
                variant="secondary"
                className="w-full mt-4"
                onClick={() => navigate('/gallery')}
              >
                View Gallery
              </Button>
            </div>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card id="recent-orders">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Recent Orders</h3>
              <Button variant="ghost" onClick={() => navigate('/gallery')}>
                View All →
              </Button>
            </div>

            {loadingOrders && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <LoadingSkeleton variant="circle" width="w-12" height="h-12" />
                    <div className="flex-1 space-y-2">
                      <LoadingSkeleton variant="text" width="w-1/3" />
                      <LoadingSkeleton variant="text" width="w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loadingOrders && recentOrders.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600 mb-4">No orders yet</p>
                <Button variant="primary" onClick={() => navigate('/upload')}>
                  Create Your First Order
                </Button>
              </div>
            )}

            {!loadingOrders && recentOrders.length > 0 && (
              <div className="space-y-3">
                {recentOrders.map((order) => {
                  const statusColor =
                    order.status === OrderStatus.COMPLETED
                      ? 'bg-green-100 text-green-700'
                      : order.status === OrderStatus.PROCESSING
                        ? 'bg-blue-100 text-blue-700'
                        : order.status === OrderStatus.FAILED
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700';

                  return (
                    <div
                      key={order.orderId}
                      className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition-all duration-200"
                      onClick={() => navigate('/gallery')}
                    >
                      <div className="flex items-center space-x-4">
                        <span className="text-3xl" role="img" aria-label={getServiceName(order.serviceType)}>
                          {getServiceIcon(order.serviceType)}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {getServiceName(order.serviceType)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatDate(order.createdAt)}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor}`}>
                        {order.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}
